/**
 * Invio form verso i sistemi interni Risparmiami / Nova Holding.
 *
 * Il token Gateway OS resta solo sul server (vedi /api/gateway).
 * Variabili d'ambiente usate dal frontend (prefisso REACT_APP_):
 * - REACT_APP_API_URL
 * - REACT_APP_FORM_RECALL_LIST_ID
 * - REACT_APP_FORM_RECALL_VICIDIAL_ACCOUNT_ID
 * - REACT_APP_FORM_RECALL_HOPPER
 * - REACT_APP_FORM_RECALL_TIPO
 * - REACT_APP_FORM_RECALL_ORIGINE
 */

function env(key: string, fallback = ""): string {
  return process.env[key] ?? fallback;
}

// ─── Configurazione ───────────────────────────────────────────────────────────

export interface RisparmiamiFormConfig {
  backendApiUrl?: string;
  formRecall?: {
    listId?: string;
    vicidialAccountId?: number;
    hopper?: boolean;
    tipo?: string;
    origine?: string;
  };
}

export const DEFAULT_CONFIG: RisparmiamiFormConfig = {
  backendApiUrl:
    env("REACT_APP_API_URL") || "https://back.risparmiami.it/api",
  formRecall: {
    listId: env("REACT_APP_FORM_RECALL_LIST_ID") || "2090",
    vicidialAccountId: Number(
      env("REACT_APP_FORM_RECALL_VICIDIAL_ACCOUNT_ID") || "1",
    ),
    hopper: env("REACT_APP_FORM_RECALL_HOPPER") !== "false",
    tipo: env("REACT_APP_FORM_RECALL_TIPO") || "API",
    origine: env("REACT_APP_FORM_RECALL_ORIGINE") || "API",
  },
};

const LEGACY_SMS_URL = "https://pontelibero.it/sms/ricezioneSms.php";
const LEGACY_GENERIC_OFFER_URL =
  "https://pontelibero.it/offerteGenerico/ricezioneOfferteGenerico.php";

// ─── Tipi ─────────────────────────────────────────────────────────────────────

export interface FormRecallFieldValues {
  nome: string;
  cognome: string;
  telefono: string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
}

export interface FormRecallLeadPayload {
  telefono: string;
  list_id: string;
  vicidial_account_id: number;
  hopper: boolean;
  nome: string;
  cognome: string;
  email?: string;
  utm_source?: string;
  utm_campagna?: string;
  utm_medium?: string;
  origine: string;
  tipo: string;
}

export interface LegacySmsLeadPayload {
  origine?: string;
  first_name: string;
  last_name: string;
  lead_email?: string;
  phone_number: string;
  source?: string;
  brand: string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
  ip: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_term?: string;
  tblci?: string;
}

export interface GenericOfferPayload {
  first_name: string;
  last_name: string;
  lead_email: string;
  phone_number: string;
  source: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  ip: string;
  origine: string;
  brand: string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
}

export interface BackendLeadPayload {
  nome: string;
  cognome?: string;
  email: string;
  telefono: string;
  messaggio?: string;
  utm_source?: string;
  utm_campaign?: string;
  privacy?: boolean;
  marketing?: boolean;
  cessione?: boolean;
  offer_id?: number;
  category?: string;
  ip?: string;
}

export interface UTMParams {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_term?: string;
}

// ─── Utility ──────────────────────────────────────────────────────────────────

export async function fetchClientIp(): Promise<string> {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip || "0.0.0.0";
  } catch {
    return "0.0.0.0";
  }
}

export function getUTMFromURL(
  search = typeof window !== "undefined" ? window.location.search : "",
): UTMParams {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
  };
}

export function persistUtmFromUrl(
  search = typeof window !== "undefined" ? window.location.search : "",
): void {
  const utm = getUTMFromURL(search);
  if (utm.utm_source) sessionStorage.setItem("utm_source", utm.utm_source);
  if (utm.utm_campaign)
    sessionStorage.setItem("utm_campaign", utm.utm_campaign);
  if (utm.utm_medium) sessionStorage.setItem("utm_medium", utm.utm_medium);
  if (utm.utm_content) sessionStorage.setItem("utm_content", utm.utm_content);
  if (utm.utm_term) sessionStorage.setItem("utm_term", utm.utm_term);
}

export function getUTMFromSessionOrURL(): UTMParams {
  if (typeof window === "undefined") return getUTMFromURL("");
  const url = getUTMFromURL(window.location.search);
  return {
    utm_source:
      url.utm_source || sessionStorage.getItem("utm_source") || undefined,
    utm_campaign:
      url.utm_campaign || sessionStorage.getItem("utm_campaign") || undefined,
    utm_medium:
      url.utm_medium || sessionStorage.getItem("utm_medium") || undefined,
    utm_content:
      url.utm_content || sessionStorage.getItem("utm_content") || undefined,
    utm_term: url.utm_term || sessionStorage.getItem("utm_term") || undefined,
  };
}

function normalizePhone(phone: string): string {
  return phone.replace(/\s/g, "");
}

function toFormUrlEncoded(data: Record<string, string>): string {
  return Object.entries(data)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
}

async function postGateway(
  action: "lead" | "consensi" | "annullamento",
  payload: Record<string, unknown>,
): Promise<Response> {
  return fetch("/api/gateway", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ action, ...payload }),
  });
}

// ─── 1. FormRecall (lead Vicidial + consensi) ─────────────────────────────────

export function buildFormRecallPayload(
  values: FormRecallFieldValues,
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
  options?: {
    origine?: string;
    tipo?: string;
    listId?: string;
    vicidialAccountId?: number;
    hopper?: boolean;
    extra?: Record<string, unknown>;
  },
): FormRecallLeadPayload & Record<string, unknown> {
  const utm = getUTMFromSessionOrURL();
  const fr = config.formRecall ?? {};

  return {
    telefono: normalizePhone(values.telefono),
    list_id: options?.listId ?? fr.listId ?? "2090",
    vicidial_account_id:
      options?.vicidialAccountId ?? fr.vicidialAccountId ?? 1,
    hopper: options?.hopper ?? fr.hopper ?? true,
    nome: values.nome.trim(),
    cognome: values.cognome.trim(),
    utm_source: utm.utm_source,
    utm_campagna: utm.utm_campaign,
    utm_medium: utm.utm_medium,
    origine: options?.origine ?? fr.origine ?? "API",
    tipo: options?.tipo ?? fr.tipo ?? "API",
    ...(options?.extra ?? {}),
  };
}

export async function submitFormRecallLead(
  payload: FormRecallLeadPayload & Record<string, unknown>,
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<void> {
  void config;
  const response = await postGateway("lead", payload);

  if (!response.ok) {
    let message = "Errore durante l'invio del form";
    try {
      const data = await response.json();
      message = data.message || data.error || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
}

export async function submitLegacyConsents(
  values: FormRecallFieldValues,
  options: { brand: string; ip: string },
): Promise<void> {
  const body = toFormUrlEncoded({
    origine: "sito",
    first_name: values.nome.trim(),
    last_name: values.cognome.trim(),
    lead_email: "non compilato",
    phone_number: normalizePhone(values.telefono),
    source: "Sito",
    brand: options.brand,
    privacy1: values.privacy1 ? "1" : "0",
    privacy2: values.privacy2 ? "1" : "0",
    privacy3: values.privacy3 ? "1" : "0",
    ip: options.ip,
  });

  const response = await fetch(LEGACY_SMS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    throw new Error("Errore registrazione consensi (legacy)");
  }
}

export async function submitGatewayConsents(
  options: {
    idsms: string;
    privacy: boolean;
    marketing: boolean;
    telecomunicazioni: boolean;
    url_form: string;
  },
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<void> {
  void config;
  const response = await postGateway("consensi", {
    idsms: String(options.idsms),
    privacy: options.privacy,
    marketing: options.marketing,
    telecomunicazioni: options.telecomunicazioni,
    url_form: options.url_form,
  });

  if (!response.ok) {
    throw new Error("Errore registrazione consensi (Gateway OS)");
  }
}

/** Flusso completo FormRecall — lead Vicidial + consensi legacy + Gateway OS */
export async function submitFormRecall(
  values: FormRecallFieldValues,
  options: {
    brand: string;
    idsms?: string | null;
    origine?: string;
    tipo?: string;
    listId?: string;
    vicidialAccountId?: number;
    hopper?: boolean;
    extra?: Record<string, unknown>;
  },
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<FormRecallLeadPayload & Record<string, unknown>> {
  const ip = await fetchClientIp();
  const leadPayload = buildFormRecallPayload(values, config, options);

  const gatewayPromise = options.idsms
    ? submitGatewayConsents(
        {
          idsms: options.idsms,
          privacy: values.privacy1,
          marketing: values.privacy2,
          telecomunicazioni: values.privacy3,
          url_form: options.brand,
        },
        config,
      ).catch((err) => console.error("Gateway consensi:", err))
    : Promise.resolve();

  await Promise.all([
    submitFormRecallLead(leadPayload, config),
    submitLegacyConsents(values, { brand: options.brand, ip }).catch((err) =>
      console.error("Legacy consensi:", err),
    ),
    gatewayPromise,
  ]);

  return leadPayload;
}

// ─── 2. Legacy SMS ────────────────────────────────────────────────────────────

export async function submitLegacySmsLead(
  payload: LegacySmsLeadPayload,
): Promise<Response> {
  const body = toFormUrlEncoded({
    origine: payload.origine ?? "sito",
    first_name: payload.first_name,
    last_name: payload.last_name,
    lead_email: payload.lead_email ?? "non compilato",
    phone_number: payload.phone_number,
    source: payload.source ?? "Sito",
    brand: payload.brand,
    privacy1: payload.privacy1 ? "1" : "0",
    privacy2: payload.privacy2 ? "1" : "0",
    privacy3: payload.privacy3 ? "1" : "0",
    ip: payload.ip,
    ...(payload.utm_source ? { utm_source: payload.utm_source } : {}),
    ...(payload.utm_campaign ? { utm_campaign: payload.utm_campaign } : {}),
    ...(payload.utm_medium ? { utm_medium: payload.utm_medium } : {}),
    ...(payload.utm_content ? { utm_content: payload.utm_content } : {}),
    ...(payload.utm_term ? { utm_term: payload.utm_term } : {}),
    ...(payload.tblci ? { tblci: payload.tblci } : {}),
  });

  const response = await fetch(LEGACY_SMS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    throw new Error("Errore nella risposta del server legacy SMS");
  }

  return response;
}

export async function submitOffertaGenericaLead(
  payload: LegacySmsLeadPayload,
  options?: { idsms?: string | null },
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<void> {
  const legacyPromise = submitLegacySmsLead(payload);

  const gatewayPromise = options?.idsms
    ? submitGatewayConsents(
        {
          idsms: options.idsms,
          privacy: payload.privacy1,
          marketing: payload.privacy2,
          telecomunicazioni: payload.privacy3,
          url_form: payload.brand,
        },
        config,
      ).catch((err) => console.error("Gateway consensi:", err))
    : Promise.resolve();

  await Promise.all([legacyPromise, gatewayPromise]);
}

// ─── 3. Offerte generiche ─────────────────────────────────────────────────────

export async function submitGenericOffer(
  payload: GenericOfferPayload,
): Promise<string> {
  const body = toFormUrlEncoded({
    first_name: payload.first_name,
    last_name: payload.last_name,
    lead_email: payload.lead_email,
    phone_number: payload.phone_number,
    source: payload.source,
    ip: payload.ip,
    origine: payload.origine,
    brand: payload.brand,
    privacy1: payload.privacy1 ? "1" : "0",
    privacy2: payload.privacy2 ? "1" : "0",
    privacy3: payload.privacy3 ? "1" : "0",
    ...(payload.utm_source ? { utm_source: payload.utm_source } : {}),
    ...(payload.utm_campaign ? { utm_campaign: payload.utm_campaign } : {}),
    ...(payload.utm_medium ? { utm_medium: payload.utm_medium } : {}),
  });

  const response = await fetch(LEGACY_GENERIC_OFFER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    throw new Error("Errore nella risposta del server offerte generiche");
  }

  return response.text();
}

// ─── 4. Disiscrizione ─────────────────────────────────────────────────────────

export async function submitDisiscrizione(
  cellulare: string,
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<{ success: boolean; message: string }> {
  const cleanPhone = cellulare.replace(/\D/g, "");

  void config;
  const response = await postGateway("annullamento", { cellulare: cleanPhone });

  const responseText = await response.text();

  if (!responseText?.trim()) {
    throw new Error("Il server non ha restituito una risposta valida");
  }

  let success = false;
  let message = "";

  try {
    const json = JSON.parse(responseText) as {
      success?: boolean | string;
      message?: string;
      error?: string;
    };
    success = json.success === true || json.success === "true";
    message = json.message || json.error || "";
  } catch {
    const params = new URLSearchParams(responseText);
    success = params.get("success") === "true";
    message = params.get("message") || "";
  }

  if (!response.ok && !message) {
    message =
      response.status === 422
        ? "Numero non valido o non presente in archivio."
        : `Errore server (${response.status}).`;
  }

  return { success, message };
}

// ─── 5. Backend Laravel ───────────────────────────────────────────────────────

export async function submitBackendLead(
  payload: BackendLeadPayload,
  config: RisparmiamiFormConfig = DEFAULT_CONFIG,
): Promise<{ message?: string; success?: boolean }> {
  const baseUrl = (config.backendApiUrl ?? "").replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error("backendApiUrl non configurato");
  }

  const response = await fetch(`${baseUrl}/leads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || "Errore nella richiesta");
  }

  return data;
}
